import type { ElementType } from 'react'
import {
  AlertTriangle,
  Building2,
  Calendar,
  CreditCard,
  FileText,
  ReceiptText,
  RotateCcw,
  Signature,
  User,
  UserCircle,
  Variable,
  Zap,
} from 'lucide-react'
import { isDottedVariablePath } from '@/lib/template-variable-tokens'

/**
 * One label/grouping taxonomy for every template-variable picker in the app,
 * covering both variable name shapes: nested dot-paths from
 * `GetWorkflowAuthoringSchema` (the current authoring vocabulary) and flat
 * PascalCase names (pre-migration rows, plus the identically-named ad-hoc
 * vocabulary `system_template_overrides.go` independently builds per send
 * site — a separate, unrelated engine that happens to reuse these names).
 */
export interface TemplateVariableGroupConfig {
  icon: ElementType
  /**
   * CSS-safe suffix for the `.template-variable-token--<slug>` chip rules in
   * styles.css. Stored rather than derived from the key because the key isn't
   * always a valid class fragment on its own ("Trigger event" lowercased is two
   * classes, not one).
   */
  slug: string
  label: string
  color: string
}

export const TEMPLATE_VARIABLE_GROUP_CONFIG = {
  Contact: {
    icon: User,
    slug: 'contact',
    label: 'Contact',
    color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  },
  Organization: {
    icon: Building2,
    slug: 'organization',
    label: 'Organization',
    color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  },
  Inquiry: {
    icon: FileText,
    slug: 'inquiry',
    label: 'Inquiry',
    color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  },
  Event: {
    icon: Calendar,
    slug: 'event',
    label: 'Event',
    color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
  },
  Actor: {
    icon: UserCircle,
    slug: 'actor',
    label: 'Sender',
    color: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20',
  },
  Signature: {
    icon: Signature,
    slug: 'signature',
    label: 'Signature',
    color: 'bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-400 border-fuchsia-500/20',
  },
  Quote: {
    icon: FileText,
    slug: 'quote',
    label: 'Quote',
    color: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20',
  },
  Invoice: {
    icon: ReceiptText,
    slug: 'invoice',
    label: 'Invoice',
    color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
  },
  Payment: {
    icon: CreditCard,
    slug: 'payment',
    label: 'Payment',
    color: 'bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-500/20',
  },
  Refund: {
    icon: RotateCcw,
    slug: 'refund',
    label: 'Refund',
    color: 'bg-lime-500/10 text-lime-700 dark:text-lime-400 border-lime-500/20',
  },
  'Trigger event': {
    icon: Zap,
    slug: 'trigger-event',
    label: 'Trigger event',
    color: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20',
  },
  Error: {
    icon: AlertTriangle,
    slug: 'error',
    label: 'Failure',
    color: 'bg-status-danger-bg text-status-danger-fg border-status-danger-border',
  },
  General: {
    icon: Variable,
    slug: 'general',
    label: 'General',
    color: 'bg-muted text-muted-foreground border-border',
  },
} satisfies Record<string, TemplateVariableGroupConfig>

/** The taxonomy's keys — the classifier, the order list and the CSS slugs all key off this. */
export type TemplateVariableGroup = keyof typeof TEMPLATE_VARIABLE_GROUP_CONFIG

export const TEMPLATE_VARIABLE_GROUP_ORDER: readonly TemplateVariableGroup[] = [
  'Contact',
  'Organization',
  'Inquiry',
  'Event',
  'Quote',
  'Invoice',
  'Payment',
  'Refund',
  'Actor',
  'Signature',
  'Trigger event',
  'Error',
  'General',
]

function isTemplateVariableGroup(value: string): value is TemplateVariableGroup {
  return value in TEMPLATE_VARIABLE_GROUP_CONFIG
}

export function getTemplateVariableGroupConfig(parentType: string): TemplateVariableGroupConfig {
  return isTemplateVariableGroup(parentType)
    ? TEMPLATE_VARIABLE_GROUP_CONFIG[parentType]
    : TEMPLATE_VARIABLE_GROUP_CONFIG.General
}

/**
 * Display position for a group. Groups missing from the order list sort LAST
 * rather than disappearing — a picker that filters on membership silently drops
 * every variable in a group someone forgot to add here.
 */
export function templateVariableGroupIndex(group: string): number {
  const index = (TEMPLATE_VARIABLE_GROUP_ORDER as readonly string[]).indexOf(group)
  return index === -1 ? TEMPLATE_VARIABLE_GROUP_ORDER.length : index
}

const DROPPED_SCOPE_SEGMENTS = new Set(['entity', 'actor'])

/**
 * Scope roots that ARE the disambiguating concept, kept as a label prefix
 * rather than dropped: there is exactly one Organization per tenant, so
 * "Organization name" reads as a whole, unambiguous label (unlike bare
 * "Name"). "trigger" reads the same way — "the event that started this
 * workflow run" is a concrete, recognizable thing, and the backend's own
 * schema labels already say "Trigger Stage" / "Trigger Event Date" for these
 * paths (see authoring_schema.go).
 *
 * `entity`/`actor` are dropped instead, not qualified: "entity" has no fixed
 * real-world referent (it's whichever record type the workflow happens to be
 * scoped to — Quote, Invoice, Booking, ...), and that entity type isn't
 * encoded in the path itself, so prefixing the literal word "entity" would
 * not disambiguate anything (`entity.total` -> "Entity total" is no more
 * meaningful than "Total").
 */
const QUALIFIED_SCOPE_LABELS: Record<string, string> = {
  org: 'Organization',
  trigger: 'Trigger',
}

/**
 * Best-effort human label for a variable with no server-provided label.
 * Dotted: `entity.contact.first_name` -> "Contact first name" (scope root
 * dropped); `org.name` -> "Organization name" (scope root qualified, not
 * dropped — see QUALIFIED_SCOPE_LABELS). Flat: `CustomerFirstName` ->
 * "Customer First Name".
 */
export function formatVariableLabel(name: string): string {
  if (isDottedVariablePath(name)) {
    const segments = name.split('.').filter(Boolean)
    const root = segments[0]
    const qualifier = segments.length > 1 ? QUALIFIED_SCOPE_LABELS[root] : undefined
    const meaningful =
      segments.length > 1 && (qualifier || DROPPED_SCOPE_SEGMENTS.has(root))
        ? segments.slice(1)
        : segments
    const words = meaningful.join(' ').replace(/_/g, ' ').trim()
    if (!words) return name
    return qualifier ? `${qualifier} ${words}` : words.charAt(0).toUpperCase() + words.slice(1)
  }
  return name
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
}

/**
 * Classifies a variable name/path into one of TEMPLATE_VARIABLE_GROUP_ORDER.
 *
 * The dotted branch matches today's nested authoring vocabulary. Entity
 * fields other than contact are server-scoped to whatever entity type is
 * actually being authored against (Quote, Invoice, Booking, ...) and can't
 * always be told apart from a bare path alone (e.g. `entity.status` exists on
 * several entities) — those fall through to General rather than guessing.
 *
 * The flat branch matches pre-migration flat PascalCase rows AND
 * system_template_overrides.go's ad-hoc vocabulary, which reuses these exact
 * names independently. Do not delete this branch without checking both.
 */
export function getTemplateVariableParentType(name: string): TemplateVariableGroup {
  if (isDottedVariablePath(name)) {
    if (name.startsWith('org.')) return 'Organization'
    if (name.startsWith('actor.')) return 'Actor'
    if (name.startsWith('signer.')) return 'Signature'
    if (name.startsWith('trigger.')) return 'Trigger event'
    if (name === 'entity.contact' || name.startsWith('entity.contact.')) return 'Contact'
    if (name.startsWith('entity.quote')) return 'Quote'
    if (
      name.startsWith('entity.invoice') ||
      name.includes('amount_due') ||
      name.includes('amount_paid')
    ) {
      return 'Invoice'
    }
    if (name.includes('event_')) return 'Event'
    return 'General'
  }
  if (name.startsWith('Customer')) return 'Contact'
  if (name.startsWith('Organization')) return 'Organization'
  if (name.startsWith('Event') || name === 'GuestCount') return 'Event'
  if (name.startsWith('Quote')) return 'Quote'
  if (name.startsWith('Invoice') || name === 'DueDate') return 'Invoice'
  if (name.startsWith('Payment')) return 'Payment'
  if (name.startsWith('Refund')) return 'Refund'
  if (name === 'FailureReason') return 'Error'
  return 'General'
}
